import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button, List, Popover, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { Resolution } from '@/api/generated/model';
import { QtyCell } from '@/components/qty';
import { reasonLabel } from '@/i18n/reason';

/**
 * Иконка причины у «Принято частично» и «Возврат» (ТЗ): наведение — причина, комментарий
 * и непринятые строки. Всё приходит в строке списка отгрузок (ARCHITECTURE §3) —
 * popover не делает запросов.
 */
export function ResolutionPopover({ resolution }: { resolution: Resolution }) {
  const { t } = useTranslation();
  const reason = reasonLabel({ code: resolution.reason_code, label: resolution.reason_label });
  const content = (
    <Space direction="vertical" style={{ maxWidth: 420 }}>
      <div>
        <Typography.Text type="secondary">{t('resolution.comment')}: </Typography.Text>
        {resolution.comment}
      </div>
      {resolution.returned_lines.length > 0 && (
        <List
          size="small"
          header={<Typography.Text strong>{t('resolution.returned')}</Typography.Text>}
          dataSource={resolution.returned_lines}
          renderItem={(l) => (
            <List.Item>
              <Space direction="vertical" size={0}>
                <Space wrap>
                  <span>{l.product_name}</span>
                  <QtyCell value={l.qty_returned} uom={l.base_uom} />
                  {l.disposition && (
                    <Tag color={l.disposition === 'restock' ? 'green' : 'red'}>
                      {t(`disposition.${l.disposition}`)}
                    </Tag>
                  )}
                </Space>
                {(l.reason_label ?? l.comment) && (
                  <Typography.Text type="secondary">
                    {[
                      l.reason_label &&
                        reasonLabel({ code: l.reason_code ?? '', label: l.reason_label }),
                      l.comment,
                    ]
                      .filter(Boolean)
                      .join(' — ')}
                  </Typography.Text>
                )}
              </Space>
            </List.Item>
          )}
        />
      )}
    </Space>
  );
  return (
    <Popover
      title={t('resolution.title', { reason })}
      content={content}
      trigger={['hover', 'click']}
    >
      <Button
        type="text"
        size="small"
        aria-label={t('resolution.iconLabel')}
        icon={<ExclamationCircleFilled style={{ color: '#fa8c16' }} />}
        onClick={(e) => {
          e.stopPropagation(); // клик по иконке не открывает карточку отгрузки
        }}
      />
    </Popover>
  );
}
